import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { PdfViewerDialogComponent } from '@app/components';
import { CompanyDocument } from '@app/models/documents/company-document.model';
import { SigningTemplateSlot } from '@app/models/documents/signing-template-slot.model';
import { OfferLetter } from '@app/models/offer-letters/offer-letter.model';
import { SlotAssignment } from '@app/modules/documents-v2/classes';
import { SigningTemplateSlotNames } from '@app/modules/documents-v2/enums/signing-template-slot-names.enum';
import { FileHelperService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { BaseForm } from '@forms/base.form';

@Component({
    selector: 'app-letter-template',
    templateUrl: './letter-template.form.html',
    styleUrls: ['./letter-template.style.scss'],
})
export class LetterTemplateForm extends BaseForm implements OnInit {
    @Input() offerLetter = new OfferLetter();
    @Input() offerLetterTemplates: CompanyDocument[] = [];

    @Input() offerLetterHasCosigners = false;
    @Output() offerLetterHasCosignersChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('viewer') pdfViewer: PdfViewerDialogComponent;

    pdfViewerOpen = false;
    assigningOrder = false;

    selectedOfferLetterTemplate: CompanyDocument | undefined;

    constructor(
        private fileHelper: FileHelperService,
        private featureService: FeatureService
    ) {
        super();
    }

    ngOnInit(): void {
        if (this.offerLetter && this.offerLetter.signingTemplate) {
            this.setSelectOfferLetterTemplate(this.offerLetter.signingTemplate.document.id);
        }
    }

    ngOnChanges(): void {
        if (this.offerLetter?.signingTemplate?.document?.id) {
            this.setSelectOfferLetterTemplate(this.offerLetter.signingTemplate.document.id);
            this.assigningOrder = this.offerLetter?.signingTemplate?.slots.some((s: SigningTemplateSlot) => s.order);
        }
    }

    compareWith(offerLetterTemplate: CompanyDocument, selectedOfferLetterTemplate: CompanyDocument): boolean {
        return offerLetterTemplate.is(selectedOfferLetterTemplate);
    }

    onSelectOfferLetterTemplate(): void {
        this.offerLetter.signingTemplateId = this.selectedOfferLetterTemplate.signingTemplate.id;
        this.offerLetter.signingTemplate = this.selectedOfferLetterTemplate.signingTemplate;
        this.offerLetter.signingTemplate.document = this.selectedOfferLetterTemplate;

        // Offer Letter Template has changed, emit new boolean value for child components
        this.offerLetterHasCosignersChange.emit(this.offerLetter.signingTemplate.hasCosigners());
    }

    onPreview(): void {
        if (!this.selectedOfferLetterTemplate) {
            return;
        }

        this.pdfViewer.setSource(this.fileHelper.path(this.selectedOfferLetterTemplate.file.id));
        this.pdfViewerOpen = true;
    }

    updateOfferLetterSlots(slotAssignments: SlotAssignment[]): void {
        const eventSlots = slotAssignments.map((slotAssignment) => slotAssignment.toSigningTemplateSlot());

        const candidateSlot = this.offerLetter.signingTemplate.slots.find(
            (slot) => slot.name === SigningTemplateSlotNames.Candidate
        );

        // From the multi signer component put the current slots to be saved.
        this.offerLetter.signingTemplate.slots = [...eventSlots];

        if (candidateSlot) {
            this.offerLetter.signingTemplate.slots.push(candidateSlot);
        }
    }

    private async setSelectOfferLetterTemplate(documentId: number): Promise<void> {
        const selectedOfferLetterTemplate = await CompanyDocument.with(['file', 'signingTemplate.slots']).find(
            documentId
        );
        this.selectedOfferLetterTemplate = selectedOfferLetterTemplate;

        // Offer Letter Template has changed, emit new boolean value for child components
        this.offerLetterHasCosignersChange.emit(this.offerLetter.signingTemplate.hasCosigners());
    }
}
